import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';

import { AuthService } from '../services/auth.service';
import {
  clientToken,
  comparisonToken,
  preferenceToken,
} from '../constants/auth-state';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {

  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    await this.auth.waitUntilInitialized();

    const requestedPath = this.getPathWithoutQuery(state.url);

    // Agent authentication (Entra / Keycloak / Legacy)
    const isAgentAuthenticated = this.auth.isAuthenticated();

    // Client routes
    if (this.isClientAccessibleRoute(requestedPath)) {

      if (clientToken()) {
        return true;
      }

      // Fill-on-behalf and other authenticated agent scenarios
      // can legitimately access these routes without client token.
      if (isAgentAuthenticated) {
        return true;
      }

      return this.router.parseUrl(
        this.buildClientLoginUrl(requestedPath)
      );
    }

    if (isAgentAuthenticated) {
      return true;
    }

    // Client trying to access agent pages
    if (clientToken()) {
      return this.router.parseUrl('/login');
    }

    // Trigger Entra / Keycloak login
    await this.auth.login();

    return false;
  }

  private isClientAccessibleRoute(path: string): boolean {
    return (
      this.isPreferenceFlowRoute(path) ||
      this.isMotorPreferenceFlowRoute(path) ||
      this.isComparisonFlowRoute(path)
    );
  }

  private isPreferenceFlowRoute(path: string): boolean {
    return path.startsWith('/preference-form');
  }

  private isMotorPreferenceFlowRoute(path: string): boolean {
    return path.startsWith('/motor/preference-form');
  }

  private isComparisonFlowRoute(path: string): boolean {
    return (
      path.startsWith('/comparison') ||
      path.startsWith('/maf')
    );
  }

  private buildClientLoginUrl(path: string): string {

    if (this.isPreferenceFlowRoute(path)) {

      const prefToken = preferenceToken();

      const redirect =
        path === '/preference-form'
          ? ''
          : `&redirect=${encodeURIComponent(path)}`;

      return prefToken
        ? `/login?token=${prefToken}${redirect}`
        : '/login';
    }

    if (this.isComparisonFlowRoute(path)) {

      const compToken = comparisonToken();

      const redirect =
        `&redirect=${encodeURIComponent(path)}`;

      return compToken
        ? `/login?token=${compToken}${redirect}`
        : '/login';
    }

    if (this.isMotorPreferenceFlowRoute(path)) {

      const prefToken = preferenceToken();

      const redirect =
        path === '/motor/preference-form'
          ? ''
          : `&redirect=${encodeURIComponent(path)}`;

      return prefToken
        ? `/login?token=${prefToken}${redirect}`
        : '/login';
    }

    return '/login';
  }

  private getPathWithoutQuery(url: string): string {
    return url.split('?')[0] || url;
  }
}